import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import { confirmationGenerationAtom } from './atoms';

/**
 * INS-11586: tells the Certify panel whether the slot rows it holds actually
 * postdate the user's last confirmation event.
 *
 * The slot list is deliberately stale-while-revalidate — rows must not blank on
 * every mutation — and `searchAtomPair` retains the previous `data` both while a
 * refetch is in flight AND on either error path. That's right for a table, but
 * the panel renders ONE row's editable state, where "briefly stale" means
 * showing reviews the user just cleared. This hook lets the panel apply a
 * stricter rule without changing the shared fetch policy.
 *
 * The rule: a fetch that SETTLES proves freshness, because every mutation
 * re-dispatches the slot-list query (`invalidateAfterConfirmationEvent`) — so
 * whatever settles next was issued after the mutation. A settle that carries an
 * error proves nothing: `loading` clears but the pre-mutation rows stay.
 *
 * @param {boolean} listLoading - Whether the slot-list fetch is in flight.
 * @param {string} [listError] - Error from the last settled fetch, if any.
 * @return {boolean} True while every row on hand predates the last mutation.
 */
export const useActiveRowStale = (
  listLoading: boolean,
  listError?: string
): boolean => {
  const mutationGeneration = useAtomValue(confirmationGenerationAtom);
  const [settledGeneration, setSettledGeneration] =
    useState(mutationGeneration);
  const wasLoading = useRef(false);

  useEffect(() => {
    const justSettled = wasLoading.current && !listLoading;
    wasLoading.current = listLoading;
    if (justSettled && !listError) setSettledGeneration(mutationGeneration);
  }, [listLoading, listError, mutationGeneration]);

  return settledGeneration < mutationGeneration;
};
